import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Trash2 } from "lucide-react";

export default function Companies() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", industry: "", pay_period: "weekly" });

  const load = () => api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/companies", form);
      toast.success("Company added");
      setOpen(false);
      setForm({ name: "", industry: "", pay_period: "weekly" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This permanently removes its employees, time entries, payroll runs and employer logins.`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      toast.success("Company deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-sky-600">Organizations</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-1">Companies</h1>
          <p className="text-slate-500 mt-1">Manage the companies you run payroll for.</p>
        </div>
        {user?.role === "admin" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-company-button" className="bg-sky-600 hover:bg-sky-700 rounded-full font-semibold"><Plus className="w-4 h-4 mr-1" /> Add company</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New company</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <Label>Company name</Label>
                  <Input data-testid="company-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>Industry</Label>
                  <Input data-testid="company-industry-input" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="mt-1.5" />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="save-company-button" className="bg-sky-600 hover:bg-sky-700">Save</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {companies.length === 0 && <p className="text-slate-400 text-sm">No companies yet.</p>}
        {companies.map((c) => (
          <div key={c.id} data-testid={`company-card-${c.id}`} className="relative rounded-2xl border border-slate-100 bg-white p-6 hover:shadow-lg hover:shadow-slate-200/60 transition-all">
            {user?.role === "admin" && (
              <button onClick={() => remove(c)} data-testid={`delete-company-${c.id}`}
                className="absolute top-4 right-4 p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors" title="Delete company">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center mb-4">
              <Building2 className="w-5 h-5" />
            </div>
            <h3 className="font-display font-bold text-lg text-slate-900">{c.name}</h3>
            <p className="text-sm text-slate-500 mt-0.5">{c.industry || "—"}</p>
            <span className="inline-block mt-4 px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold uppercase tracking-wider">{c.pay_period} pay</span>
          </div>
        ))}
      </div>
    </div>
  );
}
